import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { auditLogKeys } from '@/features/audit-log';

import {
  createCoupon,
  createPointsTier,
  getCoupon,
  getCouponRedemptions,
  getCoupons,
  getCustomerCoupons,
  getPointsTiers,
  setCouponActive,
  setPointsTierActive,
  updateCoupon,
  updatePointsTier,
} from './coupons.api';
import type {
  CouponCreateInput,
  CouponListParams,
  CouponRedemptionListParams,
  CouponUpdateInput,
  PointsTierInput,
} from '../types/coupons.types';

export const couponKeys = {
  all: ['coupons'] as const,
  lists: () => [...couponKeys.all, 'list'] as const,
  list: (countryId: number, params: CouponListParams) =>
    [...couponKeys.lists(), countryId, params] as const,
  detail: (id: number) => [...couponKeys.all, 'detail', id] as const,
  redemptions: (id: number, params: CouponRedemptionListParams) =>
    [...couponKeys.detail(id), 'redemptions', params] as const,
  customer: (customerId: number) => [...couponKeys.all, 'customer', customerId] as const,
  tiers: (countryId: number) => [...couponKeys.all, 'tiers', countryId] as const,
};

export function useCoupons(
  countryId: number | undefined,
  params: CouponListParams,
  enabled = true,
) {
  return useQuery({
    queryKey: couponKeys.list(countryId ?? 0, params),
    queryFn: ({ signal }) => getCoupons(countryId!, params, signal),
    enabled: enabled && countryId !== undefined,
    placeholderData: (previous) => previous,
  });
}

export function useCoupon(id: number | undefined) {
  return useQuery({
    queryKey: couponKeys.detail(id ?? 0),
    queryFn: ({ signal }) => getCoupon(id!, signal),
    enabled: id !== undefined,
  });
}

export function useCouponRedemptions(id: number, params: CouponRedemptionListParams) {
  return useQuery({
    queryKey: couponKeys.redemptions(id, params),
    queryFn: ({ signal }) => getCouponRedemptions(id, params, signal),
    placeholderData: (previous) => previous,
  });
}

export function useCustomerCoupons(customerId: number) {
  return useQuery({
    queryKey: couponKeys.customer(customerId),
    queryFn: ({ signal }) => getCustomerCoupons(customerId, signal),
  });
}

function useInvalidateCoupons() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: couponKeys.all });
    void queryClient.invalidateQueries({ queryKey: auditLogKeys.all });
  };
}

export function useCreateCoupon(countryId: number) {
  const invalidate = useInvalidateCoupons();
  return useMutation({
    mutationFn: (input: CouponCreateInput) => createCoupon(countryId, input),
    onSuccess: (coupon) => {
      toast.success(`Coupon ${coupon.code} created.`);
      invalidate();
    },
  });
}

export function useUpdateCoupon() {
  const invalidate = useInvalidateCoupons();
  return useMutation({
    mutationFn: (input: { id: number; values: CouponUpdateInput }) =>
      updateCoupon(input.id, input.values),
    onSuccess: (result) => {
      toast.success(result.changed > 0 ? 'Coupon updated.' : 'Nothing changed.');
      invalidate();
    },
  });
}

export function useSetCouponActive() {
  const invalidate = useInvalidateCoupons();
  return useMutation({
    mutationFn: (input: { id: number; active: boolean }) => setCouponActive(input.id, input.active),
    onSuccess: (_, input) => {
      toast.success(input.active ? 'Coupon activated.' : 'Coupon deactivated.');
      invalidate();
    },
  });
}

export function usePointsTiers(countryId: number | undefined) {
  return useQuery({
    queryKey: couponKeys.tiers(countryId ?? 0),
    queryFn: ({ signal }) => getPointsTiers(countryId!, signal),
    enabled: countryId !== undefined,
  });
}

export function useCreatePointsTier(countryId: number) {
  const invalidate = useInvalidateCoupons();
  return useMutation({
    mutationFn: (input: PointsTierInput) => createPointsTier(countryId, input),
    onSuccess: () => {
      toast.success('Points tier added.');
      invalidate();
    },
  });
}

export function useUpdatePointsTier() {
  const invalidate = useInvalidateCoupons();
  return useMutation({
    mutationFn: (input: { id: number; values: PointsTierInput }) =>
      updatePointsTier(input.id, input.values),
    onSuccess: (result) => {
      toast.success(result.changed > 0 ? 'Points tier updated.' : 'Nothing changed.');
      invalidate();
    },
  });
}

export function useSetPointsTierActive() {
  const invalidate = useInvalidateCoupons();
  return useMutation({
    mutationFn: (input: { id: number; active: boolean }) =>
      setPointsTierActive(input.id, input.active),
    onSuccess: (_, input) => {
      toast.success(input.active ? 'Points tier activated.' : 'Points tier deactivated.');
      invalidate();
    },
  });
}
